import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
/**
 * Индекс следующего трека в плейлисте, начиная со `startIndex` в направлении
 * `step` (+1 next/ended, −1 prev). Обходит плейлист по кругу; если плейлист
 * пуст — возвращает −1 (вызывающий просто останавливается).
 */
function findPlayableIndex(playlist, startIndex, step) {
    const n = playlist.length;
    if (n === 0)
        return -1;
    return ((startIndex + step) % n + n) % n;
}
const PlayerContext = createContext(undefined);
export const PlayerProvider = ({ children }) => {
    const [isPlayerOpen, setIsPlayerOpen] = useState(false);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isAudioLoading, setIsAudioLoading] = useState(false);
    const [playlist, setPlaylist] = useState([]);
    const [playMode, setPlayModeState] = useState("full");
    const audioRef = useRef(null);
    const shouldAutoPlayRef = useRef(false);
    const pendingTrackRef = useRef(null);
    // Бесшовное переключение Песня↔Минус: сохраняем позицию и флаг воспроизведения
    // до смены src того же <audio>, чтобы восстановить их после загрузки нового
    // источника (минус = тот же трек без вокала → тайминг совпадает).
    const seamlessSwitchRef = useRef(null);
    // Событие прослушивания с контекстом текущего трека (song_id обязателен,
    // остальное — если известно на месте openPlayer).
    const trackPlayerEvent = (type, extra) => {
        if (!currentTrack) {
            return;
        }
        trackEvent(type, {
            song_id: currentTrack.audioId ?? currentTrack.id,
            author_id: currentTrack.authorId,
            source: currentTrack.analyticsSource,
            position: currentTrack.analyticsPosition,
            ...extra,
        });
    };
    // Синхронизация состояния с audio элементом
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const updateTime = () => {
            if (audio.duration) {
                const progress = (audio.currentTime / audio.duration) * 100;
                setCurrentTime(progress);
            }
        };
        const handleLoadedMetadata = () => {
            if (audio.duration) {
                setDuration(audio.duration);
            }
            // Бесшовное переключение источника: как только известна длительность
            // нового src, возвращаем позицию воспроизведения на прежнюю.
            const seamless = seamlessSwitchRef.current;
            if (seamless) {
                try {
                    audio.currentTime = Math.min(seamless.time, audio.duration || seamless.time);
                }
                catch {
                    /* seek недоступен — оставляем как есть */
                }
            }
        };
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            // Трек дослушан до конца — author_song_finish с глубиной прослушивания.
            trackPlayerEvent(AnalyticsEvent.AUTHOR_SONG_FINISH, {
                listened_ms: Math.round((audio.duration || 0) * 1000),
            });
            // Автоматически переключаемся на следующий трек.
            if (currentTrack && playlist.length > 0) {
                const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
                if (currentIndex !== -1) {
                    const nextIndex = findPlayableIndex(playlist, currentIndex, 1);
                    const nextTrack = nextIndex !== -1 ? playlist[nextIndex] : null;
                    if (nextTrack) {
                        // Небольшая задержка перед переключением
                        setTimeout(() => {
                            setPlayModeState("full");
                            shouldAutoPlayRef.current = true;
                            pendingTrackRef.current = nextTrack;
                            setCurrentTrack(nextTrack);
                        }, 100);
                    }
                }
            }
        };
        const handlePlay = () => {
            // Во время бесшовного переключения источника state'ом управляем сами
            // (onCanPlay), а браузерные play/pause от перезагрузки src игнорируем —
            // иначе мигает кнопка и плодятся ложные play/pause-события аналитики.
            if (seamlessSwitchRef.current)
                return;
            setIsPlaying(true);
            trackPlayerEvent(AnalyticsEvent.AUTHOR_SONG_PLAY);
        };
        const handlePause = () => {
            if (seamlessSwitchRef.current)
                return;
            setIsPlaying(false);
            // Пауза/переключение/закрытие плеера — фиксируем глубину прослушивания.
            trackPlayerEvent(AnalyticsEvent.AUDIO_PAUSE, {
                listened_ms: Math.round((audio.currentTime || 0) * 1000),
            });
        };
        audio.addEventListener("timeupdate", updateTime);
        audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        return () => {
            audio.removeEventListener("timeupdate", updateTime);
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
        };
    }, [currentTrack]);
    // Автозапуск при загрузке нового трека
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !currentTrack)
            return;
        const tryAutoPlay = () => {
            if (shouldAutoPlayRef.current) {
                if (audio.readyState >= 2) {
                    shouldAutoPlayRef.current = false;
                    setIsPlaying(true);
                    return true;
                }
            }
            return false;
        };
        // Проверяем сразу, если audio уже готов
        if (tryAutoPlay())
            return;
        // Если еще не готов, ждем события canplay
        const handleCanPlay = () => {
            setIsAudioLoading(false);
            tryAutoPlay();
        };
        const handleCanPlayThrough = () => {
            setIsAudioLoading(false);
            tryAutoPlay();
        };
        audio.addEventListener("canplay", handleCanPlay);
        audio.addEventListener("canplaythrough", handleCanPlayThrough);
        audio.addEventListener("loadeddata", handleCanPlay);
        return () => {
            audio.removeEventListener("canplay", handleCanPlay);
            audio.removeEventListener("canplaythrough", handleCanPlayThrough);
            audio.removeEventListener("loadeddata", handleCanPlay);
        };
    }, [currentTrack]);
    // Синхронизация isPlaying с audio элементом
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        if (isPlaying) {
            audio.play().catch((error) => {
                console.error("Error playing audio:", error);
                setIsPlaying(false);
            });
        }
        else {
            audio.pause();
        }
    }, [isPlaying]);
    const openPlayer = (track, mode = "full") => {
        const isNewTrack = currentTrack?.id !== track.id;
        // Новый трек открываем в запрошенном источнике (по умолчанию — полный трек;
        // из списка можно сразу стартовать минус). Минус без ссылки откатится на
        // полный (см. вычисление src у <audio>).
        if (isNewTrack) {
            setPlayModeState(mode === "minus" && track.instrumentalUrl ? "minus" : "full");
        }
        // Если переключаемся на новый трек, останавливаем текущий
        if (isNewTrack && isPlaying) {
            setIsPlaying(false);
            if (audioRef.current) {
                audioRef.current.pause();
            }
        }
        setCurrentTrack(track);
        setIsPlayerOpen(true);
        // Если трек изменился, сбрасываем время
        if (audioRef.current && audioRef.current.src !== track.audioUrl) {
            setCurrentTime(0);
            setDuration(0);
        }
        // Автоматически запускаем воспроизведение при открытии нового трека
        if (isNewTrack) {
            shouldAutoPlayRef.current = true;
            pendingTrackRef.current = track;
            // Дополнительная проверка после обновления DOM
            setTimeout(() => {
                const audio = audioRef.current;
                if (audio && shouldAutoPlayRef.current && pendingTrackRef.current?.id === track.id) {
                    if (audio.readyState >= 2) {
                        shouldAutoPlayRef.current = false;
                        pendingTrackRef.current = null;
                        setIsPlaying(true);
                    }
                }
            }, 100);
        }
    };
    const closePlayer = () => {
        setIsPlayerOpen(false);
        setIsPlaying(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setCurrentTime(0);
    };
    const togglePlayPause = () => {
        setIsPlaying((prev) => !prev);
    };
    // Явная пауза (без тоггла) — нужна, когда стартует другой источник звука
    // (напр. видео на странице песни): аудио должно замолчать. Не трогаем src.
    const pause = () => {
        setIsPlaying(false);
        audioRef.current?.pause();
    };
    // Бесшовное переключение источника (полный↔минус). Элемент <audio> НЕ
    // перемонтируется (key = только id) — меняется лишь src. Запоминаем текущую
    // позицию и то, играл ли трек, чтобы восстановить их после загрузки нового
    // источника (см. handleLoadedMetadata + onCanPlay). Так не сбрасываются
    // время/длительность и не рассинхронивается кнопка play.
    const setPlayMode = (mode) => {
        if (mode === playMode)
            return;
        // Источник минуса резолвит вызывающий (WebPlayer): к моменту переключения
        // instrumentalUrl уже проставлен через patchCurrentTrack в том же батче.
        const audio = audioRef.current;
        seamlessSwitchRef.current = {
            time: audio ? audio.currentTime : 0,
            resume: audio ? !audio.paused : isPlaying,
        };
        setPlayModeState(mode);
    };
    // Точечно обновить поля текущего трека (напр. появившийся минус) БЕЗ смены
    // источника/перезапуска: id не меняется → audio не перемонтируется.
    const patchCurrentTrack = (patch, matchAudioId) => {
        setCurrentTrack((prev) => {
            if (!prev)
                return prev;
            if (matchAudioId != null && prev.audioId !== matchAudioId)
                return prev;
            return { ...prev, ...patch };
        });
    };
    const nextTrack = () => {
        if (!currentTrack || playlist.length === 0)
            return;
        const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
        if (currentIndex === -1)
            return;
        const nextIndex = findPlayableIndex(playlist, currentIndex, 1);
        const next = nextIndex !== -1 ? playlist[nextIndex] : null;
        if (next) {
            // Сбрасываем время и запускаем воспроизведение с начала
            setPlayModeState("full");
            setCurrentTime(0);
            setDuration(0);
            setIsAudioLoading(true);
            setCurrentTrack(next);
            setIsPlayerOpen(true);
            setIsPlaying(false);
            // Пытаемся воспроизвести сразу после обновления src
            setTimeout(() => {
                const audio = audioRef.current;
                if (!audio)
                    return;
                const playNow = () => {
                    try {
                        audio.currentTime = 0;
                        const p = audio.play();
                        if (p && typeof p.catch === "function") {
                            p.catch(() => { });
                        }
                        setIsPlaying(true);
                        setIsAudioLoading(false);
                    }
                    catch {
                        setIsAudioLoading(false);
                    }
                };
                if (audio.readyState >= 2) {
                    playNow();
                }
                else {
                    const onCanPlay = () => {
                        audio.removeEventListener("canplay", onCanPlay);
                        playNow();
                    };
                    audio.addEventListener("canplay", onCanPlay);
                }
            }, 50);
        }
    };
    const prevTrack = () => {
        if (!currentTrack || playlist.length === 0)
            return;
        const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
        if (currentIndex === -1)
            return;
        const prevIndex = findPlayableIndex(playlist, currentIndex, -1);
        const prev = prevIndex !== -1 ? playlist[prevIndex] : null;
        if (prev) {
            // Сбрасываем время и запускаем воспроизведение с начала
            setPlayModeState("full");
            setCurrentTime(0);
            setDuration(0);
            setIsAudioLoading(true);
            setCurrentTrack(prev);
            setIsPlayerOpen(true);
            setIsPlaying(false);
            // Пытаемся воспроизвести сразу после обновления src
            setTimeout(() => {
                const audio = audioRef.current;
                if (!audio)
                    return;
                const playNow = () => {
                    try {
                        audio.currentTime = 0;
                        const p = audio.play();
                        if (p && typeof p.catch === "function") {
                            p.catch(() => { });
                        }
                        setIsPlaying(true);
                        setIsAudioLoading(false);
                    }
                    catch {
                        setIsAudioLoading(false);
                    }
                };
                if (audio.readyState >= 2) {
                    playNow();
                }
                else {
                    const onCanPlay = () => {
                        audio.removeEventListener("canplay", onCanPlay);
                        playNow();
                    };
                    audio.addEventListener("canplay", onCanPlay);
                }
            }, 50);
        }
    };
    const value = {
        isPlayerOpen,
        currentTrack,
        isPlaying,
        currentTime,
        duration,
        isAudioLoading,
        playMode,
        setPlayMode,
        openPlayer,
        closePlayer,
        togglePlayPause,
        pause,
        nextTrack,
        prevTrack,
        setCurrentTime,
        setDuration,
        setIsAudioLoading,
        setPlaylist,
        patchCurrentTrack,
        audioRef,
    };
    return (_jsxs(PlayerContext.Provider, { value: value, children: [children, currentTrack && (_jsx("audio", { ref: audioRef, src: playMode === "minus" && currentTrack.instrumentalUrl
                    ? currentTrack.instrumentalUrl
                    : currentTrack.audioUrl, preload: "auto", style: { display: "none" }, onLoadStart: () => setIsAudioLoading(true), onCanPlay: () => {
                    setIsAudioLoading(false);
                    // Бесшовное переключение источника: возобновляем воспроизведение,
                    // если трек играл до смены (позицию уже вернул handleLoadedMetadata).
                    const seamless = seamlessSwitchRef.current;
                    if (seamless) {
                        seamlessSwitchRef.current = null;
                        if (seamless.resume) {
                            setIsPlaying(true);
                            audioRef.current?.play().catch(() => { });
                        }
                        return;
                    }
                    // Автозапуск при готовности
                    if (shouldAutoPlayRef.current && pendingTrackRef.current?.id === currentTrack.id) {
                        shouldAutoPlayRef.current = false;
                        pendingTrackRef.current = null;
                        setIsPlaying(true);
                    }
                }, onLoadedData: () => {
                    // Дополнительная проверка для автозапуска
                    if (shouldAutoPlayRef.current && pendingTrackRef.current?.id === currentTrack.id) {
                        const audio = audioRef.current;
                        if (audio && audio.readyState >= 2) {
                            shouldAutoPlayRef.current = false;
                            pendingTrackRef.current = null;
                            setIsPlaying(true);
                        }
                    }
                }, onError: () => {
                    setIsAudioLoading(false);
                    shouldAutoPlayRef.current = false;
                    pendingTrackRef.current = null;
                    console.error("Error loading audio");
                } }, currentTrack.id))] }));
};
export const usePlayer = () => {
    const context = useContext(PlayerContext);
    if (context === undefined) {
        throw new Error("usePlayer must be used within a PlayerProvider");
    }
    return context;
};
